import { useCallback, useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { SOCKET_URL } from '../config'
import { LocalStorageKey } from '../helpers/constants'

/**
 * Live-activity socket. Mirrors the mobile app's socket setup (same server, path,
 * transports, auth) and translates the venue events into a small set of
 * in-memory patches the map page applies to its venue list + pulses.
 */

export type VenuePatch =
  | { type: 'checkin'; venueId: string }
  | { type: 'checkout'; venueId: string }
  | { type: 'featureFlag'; key: string; payload: unknown }

interface UseMapSocketOptions {
  enabled?: boolean
  onPatch: (patch: VenuePatch) => void
  /**
   * Venue public ids to receive live activity for. The gateway only auto-joins
   * a user's checked-in venue, so the admin map must subscribe explicitly to the
   * venues it's showing (via the subscribeToVenue message).
   */
  venueIds?: string[]
}

/** 60s window to dedupe auto-checkout events (mobile dedupes vs push). */
const AUTO_CHECKOUT_DEDUP_MS = 60_000

function getAuthToken(): string | null {
  try {
    const raw = localStorage.getItem(LocalStorageKey.auth)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed?.token ?? null
  } catch {
    return null
  }
}

/**
 * Pulls a venue public id out of a socket payload. The exact shapes (verified
 * against the mobile app + backend emitters):
 *   - userCheckedInToVenue:  payload.checkInDto.venue.publicId
 *   - userCheckedOutOfVenue: payload.venuePublicId (top level)
 *   - autoCheckedOutOfVenue: payload.venuePublicId (top level)
 * The remaining lookups are defensive fallbacks.
 */
function pickVenueId(payload: any): string | null {
  if (!payload || typeof payload !== 'object') return null
  return (
    payload.venuePublicId ||
    payload.checkInDto?.venue?.publicId ||
    payload.checkInDto?.currentVenue?.publicId ||
    payload.venue?.publicId ||
    null
  )
}

export function useMapSocket({ enabled = true, onPatch, venueIds }: UseMapSocketOptions) {
  const [connected, setConnected] = useState(false)
  const onPatchRef = useRef(onPatch)
  onPatchRef.current = onPatch
  const lastAutoCheckoutRef = useRef<Map<string, number>>(new Map())

  const socketRef = useRef<Socket | null>(null)
  const subscribedRef = useRef<Set<string>>(new Set())
  const venueIdsRef = useRef<string[]>(venueIds ?? [])
  venueIdsRef.current = venueIds ?? []

  // Reconcile our venue-room subscriptions with the desired venue id set.
  const syncSubscriptions = useCallback(() => {
    const socket = socketRef.current
    if (!socket || !socket.connected) return
    const desired = new Set(venueIdsRef.current)
    const current = subscribedRef.current
    const toAdd = Array.from(desired).filter(id => !current.has(id))
    const toRemove = Array.from(current).filter(id => !desired.has(id))
    if (toAdd.length) socket.emit('subscribeToVenue', { venuePublicIds: toAdd })
    if (toRemove.length) socket.emit('unsubscribeFromVenue', { venuePublicIds: toRemove })
    subscribedRef.current = desired
  }, [])

  useEffect(() => {
    if (!enabled) return
    const token = getAuthToken()
    if (!token) return

    const socket: Socket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token },
      extraHeaders: { Authorization: `Bearer ${token}` },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 10000,
      forceNew: true,
    })
    socketRef.current = socket

    const emit = (patch: VenuePatch) => onPatchRef.current(patch)

    socket.on('connect', () => {
      setConnected(true)
      // The server has none of our subscriptions after a fresh/reconnect.
      subscribedRef.current = new Set()
      syncSubscriptions()
    })
    socket.on('disconnect', () => setConnected(false))
    socket.on('connect_error', () => setConnected(false))

    socket.on('userCheckedInToVenue', (payload: any) => {
      const venueId = pickVenueId(payload)
      if (venueId) emit({ type: 'checkin', venueId })
    })

    socket.on('userCheckedOutOfVenue', (payload: any) => {
      const venueId = pickVenueId(payload)
      if (venueId) emit({ type: 'checkout', venueId })
    })

    socket.on('autoCheckedOutOfVenue', (payload: any) => {
      const venueId = pickVenueId(payload)
      if (!venueId) return
      const now = Date.now()
      const last = lastAutoCheckoutRef.current.get(venueId) ?? 0
      if (now - last < AUTO_CHECKOUT_DEDUP_MS) return // dedup within 60s
      lastAutoCheckoutRef.current.set(venueId, now)
      emit({ type: 'checkout', venueId })
    })

    // Note: createBreadcrumb / deleteBreadcrumb payloads do NOT carry a venue id
    // (verified against the mobile app + backend emitters — the venue context
    // lives only in the socket room). We therefore can't attribute them to a
    // specific marker; breadcrumb counts refresh on the next pan/refetch instead.

    socket.on('featureFlagUpdated', (payload: any) => {
      if (payload?.key) emit({ type: 'featureFlag', key: payload.key, payload: payload.payload })
    })

    return () => {
      socket.removeAllListeners()
      socket.disconnect()
      socketRef.current = null
      subscribedRef.current = new Set()
    }
  }, [enabled, syncSubscriptions])

  // Re-sync subscriptions whenever the set of shown venues changes.
  useEffect(() => {
    syncSubscriptions()
  }, [venueIds, syncSubscriptions])

  return { connected }
}
